import React, { useEffect, useMemo, useState } from 'react'
import { api } from '../../lib/api'

function unwrap(res) {
  return res?.data?.data ?? res?.data ?? null
}

const EFFECTIVE_DATE_PREFIX = /^시행일\s*:?\s*\d{4}-\d{2}-\d{2}\s*(?:·\s*)?/
const DATED_HEADING_PREFIX = /^\d{4}-\d{2}-\d{2}\s+/

export function normalizePolicyBodyHtml(html) {
  if (typeof html !== 'string' || !html.trim() || typeof DOMParser === 'undefined') return html

  const document = new DOMParser().parseFromString(html, 'text/html')

  document.body.querySelectorAll('p').forEach((paragraph) => {
    const text = String(paragraph.textContent || '').replace(/\s+/g, ' ').trim()
    if (!EFFECTIVE_DATE_PREFIX.test(text)) return

    const remainder = text.replace(EFFECTIVE_DATE_PREFIX, '').trim()
    if (remainder) paragraph.textContent = remainder
    else paragraph.remove()
  })

  document.body.querySelectorAll('h2, h3').forEach((heading) => {
    const text = String(heading.textContent || '').replace(/\s+/g, ' ').trim()
    if (DATED_HEADING_PREFIX.test(text)) heading.textContent = text.replace(DATED_HEADING_PREFIX, '')
  })

  return document.body.innerHTML
}
/**
 * 게시된 정책 문서가 있으면 HTML로 렌더하고, 없거나 오류 시 fallback 노드를 보여준다.
 */
export default function LegalPolicyPublicSection({ docCode, fallback, showMetadata = true }) {
  const [phase, setPhase] = useState('loading')
  const [remote, setRemote] = useState(null)

  useEffect(() => {
    let cancelled = false
    setPhase('loading')
    setRemote(null)
    api
      .get(`/legal-policies/public/${encodeURIComponent(docCode)}`)
      .then((res) => {
        const d = unwrap(res)
        const html = d?.bodyHtml
        if (cancelled) return
        if (typeof html === 'string' && html.trim().length > 0) {
          setRemote(d)
          setPhase('remote')
        } else {
          setPhase('fallback')
        }
      })
      .catch(() => {
        if (!cancelled) setPhase('fallback')
      })
    return () => {
      cancelled = true
    }
  }, [docCode])

  const bodyHtml = useMemo(() => normalizePolicyBodyHtml(remote?.bodyHtml), [remote?.bodyHtml])

  if (phase === 'loading') {
    return <p className="text-sm text-slate-500">문서를 불러오는 중입니다…</p>
  }

  if (phase === 'remote' && remote) {
    return (
      <div className="legal-policy-remote">
        {showMetadata && (remote.effectiveDate || remote.versionLabel) ? (
          <p className="text-sm text-slate-500">
            {remote.effectiveDate ? '시행일: ' + remote.effectiveDate : '문서 버전: ' + remote.versionLabel}
          </p>
        ) : null}
        {remote.title ? <p className="mt-2 text-base font-semibold text-slate-900">{remote.title}</p> : null}
        {/* 운영자 입력 HTML — 신뢰 경로(CMS). 클라이언트 sanitize 없음 */}
        <div
          className="mt-6 space-y-4 text-sm leading-relaxed text-slate-800 [&_h2]:text-base [&_h2]:font-semibold [&_ul]:list-disc [&_ul]:pl-5 [&_a]:font-semibold [&_a]:text-brand-navy [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: bodyHtml }}
        />
      </div>
    )
  }

  return <>{fallback}</>
}
